"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { type ReactNode, useEffect, useRef, useState } from "react";

/**
 * HRO-009..011 — the live instrument, paired. The home's WebGL twin (fine
 * particulate data condensing into the orb, then sorting into governed
 * rings) is the strongest brand asset the site has and the least explained.
 * These three section concepts pair the real animation with the board's real
 * material so the motion carries a claim instead of an impression: a synced
 * plain-words legend, the governed answer it produces, and the records it
 * files. Each instrument mounts only when scrolled near, so the board never
 * runs WebGL it is not showing; all instances share the sequence epoch, so
 * side-by-side instruments breathe in phase.
 */
import { getTwinSequenceTime, TWIN_SEQUENCE } from "@/components/editorial/glass-twin/sequence";
import twinStyles from "@/components/editorial/glass-twin/glass-twin.module.css";

import { DATA_LABEL_CLS, DISPLAY, LABEL_CLS } from "@/components/editorial";

import type { ConceptSpec } from "../spec";

const GlassTwin3D = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwin3D,
    ),
  { ssr: false },
);

const GlassTwinIngress = dynamic(
  () =>
    import("@/components/editorial/glass-twin/glass-twin-3d").then(
      (module) => module.GlassTwinIngress,
    ),
  { ssr: false },
);

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";

/* ================================================================= */
/*  The instrument, reusable                                          */
/* ================================================================= */

/** Mounts children once the wrapper scrolls near the viewport. */
function MountInView({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || mounted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setMounted(true);
      },
      { rootMargin: "280px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mounted]);
  return (
    <div ref={ref} className={className}>
      {mounted ? children : null}
    </div>
  );
}

/** The home hero's dial, as it ships: two circles, 96 ticks, four bearings. */
const DIAL_TICKS = Array.from({ length: 96 }, (_, index) => ({
  angle: index * 3.75,
  major: index % 12 === 0,
}));

function Dial() {
  return (
    <svg
      className={twinStyles.dial}
      viewBox="0 0 560 560"
      role="presentation"
      aria-hidden="true"
    >
      <circle cx="280" cy="280" r="258" />
      <circle cx="280" cy="280" r="232" />
      {DIAL_TICKS.map(({ angle, major }) => (
        <line
          key={angle}
          x1="280"
          y1={major ? 22 : 29}
          x2="280"
          y2={major ? 48 : 40}
          transform={`rotate(${angle} 280 280)`}
        />
      ))}
      <text x="280" y="14" textAnchor="middle">0</text>
      <text x="548" y="284" textAnchor="start">90</text>
      <text x="280" y="557" textAnchor="middle">180</text>
      <text x="12" y="284" textAnchor="end">270</text>
    </svg>
  );
}

/**
 * The live instrument: the home's dial with the real WebGL twin in its hole.
 * The .gl3d-hole renders statically — it is the glass rim before the twin
 * mounts, and the section's arrival layer needs it in the DOM to aim at —
 * so only the WebGL itself waits for the viewport.
 */
export function LiveInstrument({
  scale = 2.25,
  sizeClass = "w-[min(70vw,26rem)]",
  label,
}: {
  scale?: number;
  sizeClass?: string;
  label?: string;
}) {
  return (
    <div className="min-w-0">
      <div className={`${twinStyles.particleStage} relative mx-auto aspect-square ${sizeClass}`}>
        <Dial />
        <div className="gl3d-hole">
          <MountInView className="size-full">
            <GlassTwin3D particleScale={scale} />
          </MountInView>
        </div>
      </div>
      {label ? (
        <p className={`mt-4 text-center ${INK_58} ${DATA_LABEL_CLS}`}>{label}</p>
      ) : null}
    </div>
  );
}

/**
 * The hero's stage-wide arrival: the same 2D particulate the landing hero
 * runs behind its headline, converging on this section's own glass. The
 * component finds the nearest [data-twin-stage] ancestor and absorbs into
 * the first .gl3d-hole inside it, so each concept section carries the
 * attribute and mounts exactly one instrument. Sits under the content
 * (z-1 vs z-2), exactly as the home layers it.
 */
export function IngressLayer() {
  return (
    <div
      className={`${twinStyles.particleStage} pointer-events-none absolute inset-0 z-[1] max-lg:hidden`}
      aria-hidden="true"
    >
      <MountInView className="h-full w-full">
        <GlassTwinIngress particleScale={1.9} />
      </MountInView>
    </div>
  );
}

/* ================================================================= */
/*  HRO-009 · The instrument, annotated                               */
/* ================================================================= */

/**
 * The animation's own phases, in plain words, on the home hero's timings —
 * the legend advances with the shared sequence clock, exactly as the home's
 * process caption does; reduced motion settles on the governed state.
 */
export const PHASES = [
  { startsAt: 0, title: "Connect your data", line: "Read from the systems you already use, without changing them." },
  { startsAt: TWIN_SEQUENCE.ingestSeconds, title: "Link the records", line: "Find the customers, projects and invoices that belong together." },
  { startsAt: TWIN_SEQUENCE.ingestSeconds + 6, title: "Build your Twin", line: "Bring those connections together into one picture of your business." },
  { startsAt: TWIN_SEQUENCE.ingestSeconds + 11, title: "Check your Twin", line: "Test it against your real records before you switch it on." },
] as const;

export function usePhase(): number {
  const [phase, setPhase] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(media.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      setPhase(PHASES.length - 1);
      return;
    }
    let timeout = 0;
    const update = () => {
      const elapsed = getTwinSequenceTime();
      let next = 0;
      for (let index = 1; index < PHASES.length; index += 1) {
        const step = PHASES[index];
        if (!step || elapsed < step.startsAt) break;
        next = index;
      }
      setPhase(next);
      const following = PHASES[next + 1];
      const secondsUntil = following
        ? following.startsAt - elapsed
        : TWIN_SEQUENCE.totalSeconds - elapsed;
      timeout = window.setTimeout(update, Math.max(50, secondsUntil * 1000));
    };
    update();
    return () => window.clearTimeout(timeout);
  }, [reduced]);

  return phase;
}

/**
 * What the held structure is FOR — the legend's continuation past the
 * clock: these light together with the governed phase.
 */
const USE_STEPS = [
  { startsAt: TWIN_SEQUENCE.ingestSeconds + 13, title: "Put AI to work", line: "Ask questions or have agents prepare reviews from your connected records." },
  { startsAt: TWIN_SEQUENCE.ingestSeconds + 15.5, title: "Create useful tools", line: "Keep agent results in shared channels as tables, trackers and tools." },
  { startsAt: TWIN_SEQUENCE.ingestSeconds + 18, title: "Choose your AI", line: "Use the same Twin in ChatGPT, Claude, Copilot or your own tools." },
] as const;

/**
 * The legend reads at its own pace — one row every four seconds,
 * deterministic on the shared epoch so it resets with the orb's loop —
 * while the instrument keeps its true 34s phase clock (usePhase) for the
 * exhibits that must fire when the rings settle.
 */
const LEGEND_STEPS = [...PHASES, ...USE_STEPS] as const;
const LEGEND_ROW_SECONDS = 4;

function useLegendPhase(): number {
  const [phase, setPhase] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(media.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (reduced) {
      setPhase(LEGEND_STEPS.length - 1);
      return;
    }
    let timeout = 0;
    const update = () => {
      const elapsed = getTwinSequenceTime();
      const next = Math.min(
        Math.floor(elapsed / LEGEND_ROW_SECONDS),
        LEGEND_STEPS.length - 1,
      );
      setPhase(next);
      const boundary =
        next < LEGEND_STEPS.length - 1
          ? (next + 1) * LEGEND_ROW_SECONDS - elapsed
          : TWIN_SEQUENCE.totalSeconds - elapsed;
      timeout = window.setTimeout(update, Math.max(50, boundary * 1000));
    };
    update();
    return () => window.clearTimeout(timeout);
  }, [reduced]);

  return phase;
}

/** The animation's phases as a synced list; `phase` comes from usePhase(). */
export function PhaseLegend({
  className = "",
  lines,
}: {
  className?: string;
  lines?: readonly [string, string, string, string, string, string, string];
}) {
  const phase = useLegendPhase();
  return (
    <ol className={`m-0 grid list-none gap-0 border-t border-dotted border-[var(--rule)] p-0 ${className}`}>
      {LEGEND_STEPS.map((step, index) => {
        const active = index === phase;
        const done = index < phase;
        return (
          <li
            key={step.title}
            className={`grid grid-cols-[1.6rem_9.5rem_minmax(0,1fr)] items-baseline gap-3 border-b border-dotted border-[var(--rule)] py-2.5 transition-opacity duration-500 ${active ? "opacity-100" : done ? "opacity-70" : "opacity-45"}`}
          >
            <span className={`${DATA_LABEL_CLS} ${active ? "text-[var(--theme-accent-text)]" : INK_58}`}>
              0{index + 1}
            </span>
            <span className={`text-[0.9rem] font-semibold ${INK_88}`}>{step.title}</span>
            <span className={`${INK_72} text-[0.82rem] leading-[1.5]`}>{lines?.[index] ?? step.line}</span>
          </li>
        );
      })}
    </ol>
  );
}

function AnnotatedInstrument() {
  return (
    <section data-twin-stage className="relative isolate px-5 py-12 sm:px-8">
      <IngressLayer />
      <div className="relative z-[2] mx-auto grid max-w-[74rem] items-center gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div>
          <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>
            Drawing 00 · the AI Twin, explained
          </p>
          <h2
            style={DISPLAY}
            className={`${INK_88} mt-4 max-w-[14ch] text-[clamp(2.1rem,4.5vw,3.4rem)] leading-[1.02] font-semibold tracking-[-0.035em]`}
          >
            Watch it become usable.
          </h2>
          <p className={`mt-4 max-w-[46ch] ${INK_72} text-[1rem] leading-[1.65]`}>
            The instrument is the product in one loop: your data arrives, MLX
            proposes what it means, and your team's approval sets it into
            governed structure AI can rely on.
          </p>
          <PhaseLegend className="mt-7" />
          <p className={`mt-3 ${INK_58} text-[0.72rem] leading-[1.5]`}>
            The legend advances with the animation's own 34-second clock, the
            same sequence the live hero runs; what it unlocks lights with the
            governed state, where reduced motion settles.
          </p>
        </div>
        <LiveInstrument label="Front elevation · the AI Twin" />
      </div>
    </section>
  );
}

/* ================================================================= */
/*  HRO-010 · From rings to receipts                                  */
/* ================================================================= */

/**
 * The governed answer, synced to the instrument, on the application's own
 * transcript views (instrument-plates.tsx). The production hero imports this
 * module's instrument, so the plate's product views load only where a board
 * exhibit renders it.
 */
export const CycleAnswerPlate = dynamic(() =>
  import("./instrument-plates").then((module) => module.CycleAnswerPlate),
);

/** HRO-011's records, the application's table grid (instrument-plates.tsx). */
const RenewalRecordsPlate = dynamic(() =>
  import("./instrument-plates").then((module) => module.RenewalRecordsPlate),
);

function InstrumentToAnswer() {
  return (
    <section data-twin-stage className="relative isolate px-5 py-12 sm:px-8">
      <IngressLayer />
      <div className="relative z-[2] mx-auto max-w-[74rem]">
        <div className="max-w-[44rem]">
          <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>
            The instrument, and what it is for
          </p>
          <h2
            style={DISPLAY}
            className={`${INK_88} mt-4 max-w-[16ch] text-[clamp(2rem,4.2vw,3.1rem)] leading-[1.03] font-semibold tracking-[-0.03em]`}
          >
            The rings become receipts.
          </h2>
          <p className={`mt-4 max-w-[52ch] ${INK_72} text-[1rem] leading-[1.65]`}>
            The structure the orb settles into is not decoration, it is the
            definitions your team accepted. Ask a question and the answer is
            drawn from those rings, with the receipt to prove it.
          </p>
        </div>
        <div className="mt-8 grid items-center gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <LiveInstrument sizeClass="w-[min(60vw,22rem)]" label="The structure" />
          <CycleAnswerPlate />
        </div>
      </div>
    </section>
  );
}

/* ================================================================= */
/*  HRO-011 · Chaos in, records out                                   */
/* ================================================================= */

/**
 * Raw fragments as they live in the source systems — real connector marks,
 * artifact-card styling, stuck on at slight angles. Figures echo the fixture
 * world (Tarnwick's open invoice, Cairnmoor's DPA thread, the SUN posting);
 * amounts are illustrative and every name is invented.
 */
const SOURCE_SCRAPS: {
  icon: string;
  source: string;
  title: string;
  tilt: string;
  offset: string;
}[] = [
  { icon: "xero.svg", source: "Xero", title: "INV-0027 · Tarnwick University", tilt: "-rotate-2", offset: "lg:mr-6" },
  { icon: "gmail.png", source: "Gmail", title: "RE: Cairnmoor renewal", tilt: "rotate-[1.5deg]", offset: "lg:-mt-1 lg:ml-7" },
  { icon: "sunsystems.svg", source: "SunSystems", title: "JNL 2214 · £84,000.00", tilt: "-rotate-1", offset: "lg:-mt-1 lg:mr-3" },
  { icon: "google-docs.png", source: "Docs", title: "Renewals playbook v7", tilt: "rotate-2", offset: "lg:-mt-1 lg:ml-4" },
  { icon: "slack.png", source: "Slack", title: "#renewals · 47 unread", tilt: "-rotate-[1.5deg]", offset: "lg:-mt-1 lg:mr-8" },
  { icon: "hubspot.svg", source: "HubSpot", title: "Cairnmoor Grid · deal", tilt: "rotate-1", offset: "lg:-mt-1 lg:ml-2" },
  { icon: "stripe.svg", source: "Stripe", title: "Payout · £12,304.55", tilt: "rotate-[1.5deg]", offset: "lg:-mt-1 lg:ml-9" },
  { icon: "notion.svg", source: "Notion", title: "Renewal owners table", tilt: "-rotate-2", offset: "lg:-mt-1 lg:mr-5" },
];

export function InstrumentToRecords({ padded = true, chrome = true }: { padded?: boolean; chrome?: boolean } = {}) {
  return (
    <section data-twin-stage className={`relative isolate ${padded ? "px-5 py-12 sm:px-8" : "py-12"}`}>
      <IngressLayer />
      <div className="relative z-[2] mx-auto max-w-[78rem]">
        {chrome ? (
          <div className="max-w-[44rem]">
            <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>
              One motion, three states
            </p>
            <h2
              style={DISPLAY}
              className={`${INK_88} mt-4 max-w-[16ch] text-[clamp(2rem,4.2vw,3.1rem)] leading-[1.03] font-semibold tracking-[-0.03em]`}
            >
              Chaos in. Records out.
            </h2>
          </div>
        ) : null}
        <div className="mt-8 grid items-center gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)_minmax(0,1.15fr)]">
          <div className="min-w-0">
            {/* Compact one-line chips in the product's resource-card idiom
                (the mark in a soft tile), stuck on at slight angles. Wrapped
                row on stacked layouts, a staggered column beside the
                instrument from lg. */}
            <ul className="m-0 flex list-none flex-wrap items-start gap-2 p-0 lg:flex-col lg:flex-nowrap">
              {SOURCE_SCRAPS.map((scrap) => (
                <li
                  key={scrap.title}
                  className={`${scrap.tilt} ${scrap.offset} flex w-fit items-center gap-1.5 rounded-lg border border-[color-mix(in_srgb,var(--theme-ink)_12%,transparent)] bg-white py-1.5 pr-3 pl-1.5 whitespace-nowrap shadow-sm`}
                >
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-[4px] bg-[color-mix(in_srgb,var(--theme-ink)_5%,transparent)]">
                    <Image
                      src={`/concepts/source-icons/${scrap.icon}`}
                      width={12}
                      height={12}
                      alt=""
                      loading="lazy"
                      className="size-3 object-contain"
                    />
                  </span>
                  <span className={`text-[0.6rem] font-semibold tracking-[0.1em] uppercase ${INK_58}`}>{scrap.source}</span>
                  <span className={`text-[0.72rem] leading-none font-semibold ${INK_88}`}>{scrap.title}</span>
                </li>
              ))}
            </ul>
            <p className={`mt-3 text-center ${INK_58} ${DATA_LABEL_CLS}`}>Scattered across your systems</p>
          </div>
          <LiveInstrument sizeClass="w-[min(56vw,20rem)]" label="The Twin organises" />
          <div className="min-w-0">
            <RenewalRecordsPlate />
            <p className={`mt-3 text-center ${INK_58} ${DATA_LABEL_CLS}`}>
              Filed as records you keep
            </p>
          </div>
        </div>
        {chrome ? (
          <p className={`mx-auto mt-6 max-w-[62ch] text-center ${INK_72} text-[0.9rem] leading-[1.6]`}>
            The same motion the hero performs, read literally: what your tools
            scatter, the Twin organises, and files into tables you define.
          </p>
        ) : null}
      </div>
    </section>
  );
}

/* ================================================================= */

export const INSTRUMENT_PAIRING_SPECS: ConceptSpec[] = [
  {
    id: "HRO-009",
    title: "The instrument, annotated",
    note: "The live WebGL twin beside a plain-words legend that advances on the animation's own 34-second clock, the hero made legible at second zero. The hero's stage-wide arrival layer runs across the whole section, feeding the glass from outside the dial. Reduced motion settles on the governed state.",
    surface: "ground",
    width: "full",
    interaction: "prototype",
    state: "Live · synced legend",
    render: () => <AnnotatedInstrument />,
  },
  {
    id: "HRO-010",
    title: "From rings to receipts",
    note: "The live instrument paired with its payoff: the governed answer as a night asset, real renderer and receipt. Each time the rings settle into the governed phase, the answer re-streams through the product's own wipe, so the motion and the answer read as one loop.",
    surface: "ground",
    width: "full",
    interaction: "prototype",
    state: "Live · answer",
    render: () => <InstrumentToAnswer />,
  },
  {
    id: "HRO-011",
    title: "Chaos in, records out",
    note: "Three states in one row: source scraps with their real marks stuck on at angles, the live instrument organising between them, and the real record grid, with the arrival layer flying particles across the scraps into the glass, so the records message is told by the hero's own motion.",
    surface: "ground",
    width: "full",
    interaction: "prototype",
    state: "Live · records",
    render: () => <InstrumentToRecords />,
  },
];
